import React, { useState, useCallback, useMemo, useRef, useEffect, useSyncExternalStore } from 'react';
import { TransportCommand } from '../transport/TransportCommand';
import { QueueController, msSinceLastPlayTrackCompleted } from '../transport/QueueController';
import { REMOTE_ACCESS_ENABLED } from '../config/featureFlags';
import { useMaestroStore, useDiscoveredZones, useActiveZone, useTunnelConnected } from '../store/maestroStore';
import { useActiveTrack, isRadioActive as getIsRadioActive } from '../transport/PlaybackSnapshot';
import QueuePanel from './QueuePanel';
import MultiZoneDevicePicker from './MultiZoneDevicePicker';
import MultiZoneVolumePanel from './MultiZoneVolumePanel';
import { useTranslation } from '../i18n/useTranslation';
import { useUI } from '../providers/UIProvider';
import usePeerNetwork from '../hooks/usePeerNetwork';
import PeerPresenceBar from './player/PeerPresenceBar';
import useAppNavigation from '../hooks/useAppNavigation';
import { useZoneProfileStore } from '../store/zoneProfileStore';
import useAudioOutputDevices from '../hooks/useAudioOutputDevices';
import { isConnectorAvailable, getAudioOutputDevices, setAudioOutputDevice, setExclusiveMode } from '../lib/connector';
import { userExplicitlyChoseSystemAudio } from '../utils/localZoneStorage';
import { isIOSNativeApp, isDesktopMacOSUserAgent } from '../utils/platform';
import { getUsbDacDisplayName, isExclusiveModeEnabled } from '../utils/usbDacNames';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { getClientTransport } from '../handlers/ClientTransportHandler';
import { getDeliveredFormatLabel } from '../services/StreamRouter';
import { ZoneController } from '../transport/ZoneController';
import useMultizonePlayback from '../hooks/useMultizonePlayback';
import { formatDeviceLabelWithConnectivity } from '../transport/rendererConnectivity';
import { resolveZoneDisplayName } from '../utils/zoneDisplayName';
import { formatPlaybackQualityLabel } from '../utils/audioQuality';
import { useResolvedTrackArtwork } from '../hooks/useResolvedTrackArtwork';
import { getDisplayVolume } from '../transport/volumeHydration';
import { formatOutputPathSuffix } from '../utils/outputPathBadge';
import SignalPathBadge from './SignalPathBadge';
import { useSignalPathNudge } from '../hooks/useSignalPathNudge';
import { isExclusiveHogUnavailableError } from '../utils/exclusiveFallbackUI';
import { bitPerfectBlockedMessage, isConnectorBitPerfectBlocked } from '../utils/connectorCapability';
import { formatLocalNativeDsdBadge } from '../utils/dsdOutputBadge';
import { getDsdLabel, getDsdSampleRateHz, isDsd } from '../utils/formatDetection';

const GOLD = '#C9A84C';

// Module-scoped debounce for play/pause — survives re-renders, callback recreation,
// and the iOS WKWebView touchend→click double-fire where React swaps the button
// between events (touchend fires pause → re-render → click fires play).
const _lastPlayPauseAccepted = { at: 0 };
const PLAY_PAUSE_DEBOUNCE_MS = 400;

const fmt = (s) => {
  const sec = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, '0')}`;
};

export default function MiniPlayer({ isScrubbingRef }) {
  const { t } = useTranslation();
  const nav = useAppNavigation();

  // --- Store selectors (fine-grained) ---
  const playbackState = useMaestroStore((s) => s.playbackState);
  const audioStreamBuffering = useMaestroStore((s) => s.audioStreamBuffering);
  const currentTrack = useMaestroStore((s) => s.currentTrack);
  const position = useMaestroStore((s) => s.position);
  const duration = useMaestroStore((s) => s.duration);
  const volume = useMaestroStore((s) => s.volume);
  const queue = useMaestroStore((s) => s.queue);
  const queueIndex = useMaestroStore((s) => s.queueIndex);
  const zoneName = useMaestroStore((s) => s.system.zoneName);
  const activeSource = useMaestroStore((s) => s.activeSource);
  const activeConnectorRenderer = useMaestroStore((s) => s.activeConnectorRenderer);
  const selectedUsbDac = useMaestroStore((s) => s.selectedUsbDac);
  const exclusiveError = useMaestroStore((s) => s.exclusiveError);
  const transportPlaybackError = useMaestroStore((s) => s.transportPlaybackError);
  const usbOutputPath = useMaestroStore((s) => s.usbOutputPath);
  const exclusiveDsdOutputMode = useMaestroStore((s) => s.exclusiveDsdOutputMode);
  const exclusiveSourceDsdRateHz = useMaestroStore((s) => s.exclusiveSourceDsdRateHz);
  const usbOutputFallbackNotice = useMaestroStore((s) => s.usbOutputFallbackNotice);
  const rendererConnectivityState = useMaestroStore((s) => s.rendererConnectivityState);
  const unreachablePlayPrompt = useMaestroStore((s) => s.unreachablePlayPrompt);
  const shuffled = useMaestroStore((s) => s.shuffled);

  // Quiet exclusive→shared fallback notice auto-dismisses after 8s.
  useEffect(() => {
    if (!usbOutputFallbackNotice) return undefined;
    const timer = setTimeout(() => {
      useMaestroStore.getState().setUsbOutputFallbackNotice(null);
    }, 8000);
    return () => clearTimeout(timer);
  }, [usbOutputFallbackNotice]);

  // Cross-device sync via ClientSyncHandler (real-time via BATON WS)
  const syncHandlerRef = useRef(getSyncHandler());
  const _syncVersion = useSyncExternalStore(
    (cb) => syncHandlerRef.current.onObserverStateChanged(cb),
    () => {
      const sh = syncHandlerRef.current;
      const rs = sh.getRemoteSession();
      return rs ? `${rs.controllerId}|${rs.playbackState}|${rs.rendererName}` : '';
    },
  );
  const remoteSession = syncHandlerRef.current.getRemoteSession();
  const isObserving = syncHandlerRef.current.isObserving();

  const isRadioActive = getIsRadioActive();
  const displayTrack = useActiveTrack();
  const artworkUrl = useResolvedTrackArtwork(currentTrack);
  const displayVolume = getDisplayVolume(volume, activeConnectorRenderer?.id);

  // --- Zone / discovery ---
  const discoveredZones = useDiscoveredZones();
  const activeZone = useActiveZone();
  const { showDevicePicker, setShowDevicePicker, showQueueSheet, setShowQueueSheet, showToast } = useUI();
  const openZonePicker = useCallback(() => setShowDevicePicker(true), [setShowDevicePicker]);
  const signalPathNudge = useSignalPathNudge(openZonePicker);
  const audioOutputs = useAudioOutputDevices();
  const tunnelConnected = useTunnelConnected();
  const { selfOnline, remotePeers } = usePeerNetwork();
  const isSecurelyConnected = REMOTE_ACCESS_ENABLED && (selfOnline || tunnelConnected);

  const isNativeiOS = useMemo(() => isIOSNativeApp(), []);

  // --- Multizone ---
  const {
    multizoneEnabled, masterVolume: mzMasterVolume, groupedZones: mzGroupedZones,
    enableMultizone, disableMultizone, toggleZoneInGroup,
    setMasterVolume: setMzMasterVolume, setZoneVolume: setMzZoneVolume,
  } = useMultizonePlayback();

  // --- Bridge renderers (for zone picker) ---
  // We still need renderer discovery from usePlaybackBridge for the zone picker.
  // Import the bridge renderers from the store's discoveredZones.
  const bridgeRenderers = useMemo(() => {
    return discoveredZones.filter(z => z.type !== 'browser' && z.type !== 'local' && z.type !== 'local-native');
  }, [discoveredZones]);

  // --- Local UI state ---
  const [showVolume, setShowVolume] = useState(false);
  const [isDraggingVolume, setIsDraggingVolume] = useState(false);
  const volumeFadeRef = useRef(null);
  const [showMzVolume, setShowMzVolume] = useState(false);
  const toggleMzVolume = useCallback(() => setShowMzVolume((v) => !v), []);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubTime, setScrubTime] = useState(0);

  // --- Derived playback state ---
  const effectivePosition = position;
  const effectiveDuration = duration;
  const scrubberMax = effectiveDuration > 0
    ? effectiveDuration
    : Math.max(100, Math.ceil((isScrubbing ? scrubTime : effectivePosition) + 30));
  const displayTime = isScrubbing ? scrubTime : effectivePosition;
  const progressPct = effectiveDuration > 0 ? (displayTime / effectiveDuration) * 100 : 0;
  const isPlaying =
    playbackState === 'playing' || playbackState === 'loading' || playbackState === 'buffering';

  const source = isRadioActive ? 'radio' : currentTrack ? 'library' : 'idle';
  const status =
    playbackState === 'playing'
      ? 'playing'
      : playbackState === 'paused'
        ? 'paused'
        : playbackState === 'loading' || playbackState === 'buffering'
          ? 'loading'
          : 'idle';

  const canSkip = isRadioActive ? true : queue.length > 0 && queueIndex < queue.length - 1;
  const canPrev = isRadioActive ? false : queue.length > 0;

  const isLocalNativeZone = activeZone?.type === 'local-native';
  const forcePcmRef = useRef(false);
  useEffect(() => {
    if (!isLocalNativeZone) return;
    import('../lib/connector').then(({ getForcePcmForDsd }) =>
      getForcePcmForDsd().then((v) => { forcePcmRef.current = v; }).catch(() => {})
    ).catch(() => {});
  }, [isLocalNativeZone, currentTrack?.id]);

  const outputPathSuffix = formatOutputPathSuffix(t, {
    usbOutputPath,
    isLocalNativeZone,
    isPlaybackLive: isPlaying,
    exclusiveEngineActive: TransportCommand.isExclusiveActive(),
    exclusiveSuppressed: isConnectorBitPerfectBlocked(),
  });

  const formatBadge = (() => {
    if (multizoneEnabled && mzGroupedZones.length >= 2) {
      return 'CD Quality (multizone)';
    }
    const trackUrl = currentTrack?.url || currentTrack?.streamUrl || '';
    const isDsdTrack = isDsd(currentTrack)
      || /\.(dsf|dff|dsdiff)(\?|$)/i.test(trackUrl);
    if (isDsdTrack && isLocalNativeZone) {
      return formatLocalNativeDsdBadge({
        exclusiveDsdOutputMode,
        exclusiveSourceDsdRateHz,
        trackDsdLabel: getDsdLabel(currentTrack) || 'DSD',
        forcePcmForDsd: forcePcmRef.current,
        trackSourceDsdHz: getDsdSampleRateHz(currentTrack),
        outputPathSuffix,
      });
    }
    const delivered = getDeliveredFormatLabel();
    if (delivered) {
      return `${delivered}${outputPathSuffix}`;
    }
    const f = currentTrack?.format || 'FLAC';
    const quality = formatPlaybackQualityLabel(currentTrack);
    const base = quality ? `${f} · ${quality}` : f;
    return `${base}${outputPathSuffix}`;
  })();

  const isBridgeActive = !!(isConnectorAvailable() || isNativeiOS);
  const connectorVersion = useMemo(() => (window).__MAESTRO_CONNECTOR__?.version || '', []);

  // Auto-select USB DAC as default output on Connector startup (all platforms)
  const usbAutoSelectedRef = useRef(false);
  React.useEffect(() => {
    if (usbAutoSelectedRef.current) return;
    if (!isConnectorAvailable()) return;
    if (userExplicitlyChoseSystemAudio()) return;
    const activeRenderer = useMaestroStore.getState().activeConnectorRenderer;
    if (activeRenderer) return;

    const isMac = isDesktopMacOSUserAgent();
    let cancelled = false;
    getAudioOutputDevices().then((devices) => {
      if (cancelled || usbAutoSelectedRef.current) return;
      // macOS: filter by transport_type; Windows: cpal marks all as is_likely_dac=true,
      // exclude known built-in device names
      const usbDac = devices.find((d) => {
        if (isMac) return d.transport_type === 'usb' && (d.is_likely_dac !== false);
        if (!d.is_likely_dac) return false;
        const n = d.name.toLowerCase();
        const builtIn = ['speakers', 'realtek', 'built-in', 'headphones', 'hdmi',
          'displayport', 'digital audio', 'microsoft sound mapper'];
        return !builtIn.some((kw) => n.includes(kw));
      });
      if (usbDac) {
        if (isMac && !usbDac.is_default) {
          setAudioOutputDevice(usbDac.id).catch(() => {});
        }
        // On Windows: setSinkId routing happens when user plays; store the selection
        useMaestroStore.getState().setSelectedUsbDac({ id: usbDac.id, name: getUsbDacDisplayName(usbDac), uid: usbDac.uid });
        usbAutoSelectedRef.current = true;

        if (isMac && usbDac.uid && isExclusiveModeEnabled(usbDac.uid)) {
          setExclusiveMode(usbDac.id, true).catch((err) =>
            console.warn('[MiniPlayer] Auto-enable exclusive mode failed:', err)
          );
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 480);
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 479px)');
    const handler = (e) => setIsNarrow(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // --- Zone picker ---
  const defaultZones = isNativeiOS
    ? [{ id: 'this_device', name: t('player.iphone'), type: 'local' }]
    : [{ id: 'this_computer', name: t('player.this_computer'), type: 'local' }];
  const batonZones = discoveredZones?.length > 0 ? discoveredZones : defaultZones;
  const allZones = batonZones;
  const uniqueNetworkZones = useMemo(() => {
    return batonZones.filter(z =>
      z.type !== 'local' && z.type !== 'browser' && z.type !== 'local-native' &&
      z.id !== 'this_computer' && z.id !== 'local_computer' && z.id !== 'this_device'
    );
  }, [batonZones]);

  const deviceLabelRaw = useMemo(() => {
    if (multizoneEnabled && mzGroupedZones.length >= 2) {
      return t('player.multi_zone_count', { count: mzGroupedZones.length });
    }
    if (activeZone?.id) {
      const profiles = useZoneProfileStore.getState().zones;
      const profile = profiles.find(p => p.zone_id === activeZone.id);
      const base = resolveZoneDisplayName(activeZone, {
        profileDisplayName: profile?.display_name,
        discoveredZones: batonZones,
        selectedUsbDac: useMaestroStore.getState().selectedUsbDac,
        t,
      }) || zoneName || t('player.system_output');
      return formatDeviceLabelWithConnectivity(base, rendererConnectivityState, t);
    }
    return activeZone?.name || zoneName || t('player.system_output');
  }, [multizoneEnabled, mzGroupedZones.length, activeZone, zoneName, rendererConnectivityState, batonZones, t]);
  const deviceLabel = deviceLabelRaw.length > 24 ? deviceLabelRaw.slice(0, 22) + '…' : deviceLabelRaw;

  // Resolve fixedVolume defensively: prefer activeZone.capabilities, but fall back to
  // the full zone in discoveredZones if a partial zone object ever got written to
  // activeZone (e.g. a sync-handler shim). This guarantees AirLens etc. stay grayed out.
  const fixedVolume = useMemo(() => {
    if (activeZone?.capabilities?.fixedVolume) return true;
    const zid = activeZone?.id;
    if (!zid) return false;
    const match = discoveredZones.find((z) => z.id === zid);
    return !!match?.capabilities?.fixedVolume;
  }, [activeZone, discoveredZones]);

  // --- Command handlers ---
  const blockBitPerfectPlay = useCallback(() => {
    const active = useMaestroStore.getState().discovery?.activeZone;
    if (active?.type === 'local-native' && isConnectorBitPerfectBlocked()) {
      showToast(bitPerfectBlockedMessage() || 'Bit-perfect playback requires a newer Connector — please update.', 5000);
      return true;
    }
    return false;
  }, [showToast]);

  const handlePlayPause = useCallback(() => {
    const now = Date.now();
    if (now - _lastPlayPauseAccepted.at < PLAY_PAUSE_DEBOUNCE_MS) {
      console.warn('[MiniPlayer] play/pause debounced — ignoring rapid repeat (module-scoped)', {
        gap: now - _lastPlayPauseAccepted.at,
        playbackState,
      });
      return;
    }
    _lastPlayPauseAccepted.at = now;

    if (blockBitPerfectPlay()) return;

    getSyncHandler().claimControl();
    if (playbackState === 'playing' || playbackState === 'loading') {
      TransportCommand.pause(true);
    } else {
      TransportCommand.play();
    }
  }, [playbackState, blockBitPerfectPlay]);

  // --- Connector: ghost playback watchdog (true elapsed stalls only — not zone list churn) ---
  // Disabled on iOS: NativeQueueManager owns the playback lifecycle for network renderers.
  const stallAnchorRef = useRef({ pos: 0, at: 0 });
  const watchdogResumeCooldownUntilRef = useRef(0);

  React.useEffect(() => {
    if (isIOSNativeApp()) {
      stallAnchorRef.current = { pos: 0, at: Date.now() };
      return undefined;
    }

    const tick = () => {
      if (isScrubbing || isScrubbingRef?.current) return;

      const s = useMaestroStore.getState();
      const pos = s.position ?? 0;
      const now = Date.now();

      if (
        s.playbackState !== 'playing'
        || !s.rendererHasStream
        || !s.activeConnectorRenderer
        || !s.currentTrack
        || (s.duration || 0) <= 0
      ) {
        stallAnchorRef.current = { pos, at: now };
        return;
      }

      if (msSinceLastPlayTrackCompleted() < 5000) {
        stallAnchorRef.current = { pos, at: now };
        return;
      }

      const anchor = stallAnchorRef.current;
      if (Math.abs(pos - anchor.pos) > 0.5) {
        stallAnchorRef.current = { pos, at: now };
        return;
      }
      if (now - anchor.at < 12000) return;
      if (now < watchdogResumeCooldownUntilRef.current) return;

      // Observed stall only — never issue transport commands from watchdog logic.
      console.warn(
        '[Watchdog] Ghost playback stall — position frozen ≥12s while store says playing (no auto-resume)',
      );
      watchdogResumeCooldownUntilRef.current = now + 60_000;
      stallAnchorRef.current = { pos, at: now };
    };

    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isScrubbing, isScrubbingRef]);

  const seekDebounceRef = useRef(null);
  const handleSeek = useCallback((pos) => {
    clearTimeout(seekDebounceRef.current);
    seekDebounceRef.current = setTimeout(() => {
      // Clamp seek to 5s before end to prevent triggering track-advance
      const dur = useMaestroStore.getState().duration || 0;
      const safePos = dur > 10 ? Math.min(pos, dur - 5) : pos;
      TransportCommand.seek(safePos);
    }, 100);
  }, []);

  const sendVolumeCommand = useCallback((level) => {
    const isLocalZone = !activeConnectorRenderer;
    if (isNativeiOS && isLocalZone) {
      try {
        (window).__maestroNative?.setSystemVolume(level / 100);
      } catch {}
    }
    TransportCommand.volume(level);
  }, [activeConnectorRenderer, isNativeiOS]);

  // Measure only the playbar chrome (container + peer bar), not overlays like the device picker.
  const miniPlayerMeasureRef = useRef(null);
  const MINI_PLAYER_HEIGHT_MIN = 72;
  const MINI_PLAYER_HEIGHT_MAX = 280;

  useEffect(() => {
    const el = miniPlayerMeasureRef.current;
    if (!el) return;

    const applyHeight = (raw) => {
      const clamped = Math.ceil(
        Math.min(MINI_PLAYER_HEIGHT_MAX, Math.max(MINI_PLAYER_HEIGHT_MIN, raw || MINI_PLAYER_HEIGHT_MIN)),
      );
      document.documentElement.style.setProperty('--mini-player-height', `${clamped}px`);
    };

    const ro = new ResizeObserver(([entry]) => {
      const raw = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      applyHeight(raw);
    });
    ro.observe(el);
    applyHeight(el.getBoundingClientRect().height);

    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty('--mini-player-height', `${MINI_PLAYER_HEIGHT_MIN}px`);
    };
  }, [source, isNarrow, remotePeers.length, isSecurelyConnected]);

  const volTrackRef = useRef(null);
  const volFillRef = useRef(null);
  const volThumbRef = useRef(null);
  const volLabelRef = useRef(null);
  const volDraggingRef = useRef(false);
  const volThrottleRef = useRef(0);
  const volLastSentRef = useRef(-1);
  const TRACK_H = 240;

  const volFromY = useCallback((clientY) => {
    const track = volTrackRef.current;
    if (!track) return displayVolume;
    const rect = track.getBoundingClientRect();
    const pct = 1 - Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    return Math.round(pct * 100);
  }, [volume, displayVolume]);

  const volUpdateDOM = useCallback((level) => {
    if (volFillRef.current) volFillRef.current.style.height = `${level}%`;
    if (volThumbRef.current) volThumbRef.current.style.bottom = `calc(${level}% - 16px)`;
    if (volLabelRef.current) volLabelRef.current.textContent = String(level);
  }, []);

  const volSendThrottled = useCallback((level) => {
    const now = Date.now();
    if (now - volThrottleRef.current < 30) return;
    volThrottleRef.current = now;
    if (level !== volLastSentRef.current) {
      volLastSentRef.current = level;
      sendVolumeCommand(level);
    }
  }, [sendVolumeCommand]);

  const volOnTouchStart = useCallback((e) => {
    e.preventDefault();
    volDraggingRef.current = true;
    setIsDraggingVolume(true);
    TransportCommand.startVolumeDrag();
    clearTimeout(volumeFadeRef.current);
    const level = volFromY(e.touches[0].clientY);
    volUpdateDOM(level);
    volSendThrottled(level);
  }, [volFromY, volUpdateDOM, volSendThrottled]);

  const volOnTouchMove = useCallback((e) => {
    if (!volDraggingRef.current) return;
    e.preventDefault();
    const level = volFromY(e.touches[0].clientY);
    volUpdateDOM(level);
    volSendThrottled(level);
  }, [volFromY, volUpdateDOM, volSendThrottled]);

  const volOnTouchEnd = useCallback((e) => {
    if (!volDraggingRef.current) return;
    volDraggingRef.current = false;
    TransportCommand.endVolumeDrag();
    const touch = e.changedTouches?.[0];
    if (touch) {
      const level = volFromY(touch.clientY);
      volUpdateDOM(level);
      sendVolumeCommand(level);
    }
    clearTimeout(volumeFadeRef.current);
    volumeFadeRef.current = setTimeout(() => setIsDraggingVolume(false), 1000);
  }, [volFromY, volUpdateDOM, sendVolumeCommand]);

  const volOnMouseDown = useCallback((e) => {
    e.preventDefault();
    volDraggingRef.current = true;
    setIsDraggingVolume(true);
    TransportCommand.startVolumeDrag();
    clearTimeout(volumeFadeRef.current);
    const level = volFromY(e.clientY);
    volUpdateDOM(level);
    volSendThrottled(level);
    const onMove = (ev) => {
      if (!volDraggingRef.current) return;
      const l = volFromY(ev.clientY);
      volUpdateDOM(l);
      volSendThrottled(l);
    };
    const onUp = (ev) => {
      volDraggingRef.current = false;
      TransportCommand.endVolumeDrag();
      const l = volFromY(ev.clientY);
      volUpdateDOM(l);
      sendVolumeCommand(l);
      clearTimeout(volumeFadeRef.current);
      volumeFadeRef.current = setTimeout(() => setIsDraggingVolume(false), 1000);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [volFromY, volUpdateDOM, volSendThrottled, sendVolumeCommand]);

  const renderVolumePopover = useCallback(() => (
    <>
      <div className="fixed inset-0 z-[111]" onClick={() => setShowVolume(false)} />
      <div
        className="volume-slider-popover absolute bottom-full right-0 mb-2 z-[112] rounded-2xl shadow-lg flex flex-col items-center"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', width: 72, padding: '14px 0' }}
      >
        <span
          ref={volLabelRef}
          className="text-[15px] font-bold tabular-nums"
          style={{
            color: isDraggingVolume ? '#C9A84C' : 'var(--text-secondary)',
            transition: 'color 0.2s',
            marginBottom: 10,
          }}
        >
          {displayVolume}
        </span>
        <div
          ref={volTrackRef}
          style={{
            position: 'relative',
            width: 56,
            height: TRACK_H,
            cursor: 'pointer',
            touchAction: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
          }}
          onTouchStart={volOnTouchStart}
          onTouchMove={volOnTouchMove}
          onTouchEnd={volOnTouchEnd}
          onMouseDown={volOnMouseDown}
        >
          <div style={{
            position: 'absolute', left: '50%', transform: 'translateX(-50%)',
            width: 8, height: TRACK_H, borderRadius: 4,
            background: 'var(--border)',
          }} />
          <div
            ref={volFillRef}
            style={{
              position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)',
              width: 8, borderRadius: 4,
              background: '#C9A84C',
              height: `${displayVolume}%`,
              transition: volDraggingRef.current ? 'none' : 'height 0.1s ease-out',
            }}
          />
          <div
            ref={volThumbRef}
            style={{
              position: 'absolute', left: '50%', transform: 'translateX(-50%)',
              width: 32, height: 32, borderRadius: '50%',
              background: 'var(--text-primary)',
              boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
              bottom: `calc(${displayVolume}% - 16px)`,
              transition: volDraggingRef.current ? 'none' : 'bottom 0.1s ease-out',
            }}
          />
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--text-tertiary)" style={{ marginTop: 10 }}>
          <path d="M3 9V15H7L12 20V4L7 9H3Z" />
        </svg>
      </div>
    </>
  ), [volume, isDraggingVolume, volOnTouchStart, volOnTouchMove, volOnTouchEnd, volOnMouseDown]);

  const handleNext = useCallback(() => {
    getSyncHandler().claimControl();
    QueueController.next();
  }, []);

  const handlePrev = useCallback(() => {
    getSyncHandler().claimControl();
    QueueController.previous();
  }, []);

  const handleToggleShuffle = useCallback(() => {
    const store = useMaestroStore.getState();
    if (store.shuffled) {
      store.setShuffled(false, []);
    } else {
      const len = store.queue.length;
      if (len === 0) return;
      const order = Array.from({ length: len }, (_, i) => i);
      // Keep current track at position 0, shuffle the rest
      const current = store.queueIndex;
      order[0] = current;
      const rest = order.filter((_, i) => i !== 0).map(v => v === current ? 0 : v);
      // Fisher-Yates on rest
      for (let i = rest.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [rest[i], rest[j]] = [rest[j], rest[i]];
      }
      const shuffleOrder = [current, ...rest];
      store.setShuffled(true, shuffleOrder);
    }
  }, []);

  const handleStop = useCallback(() => {
    useMaestroStore.getState().setPlaybackState('stopped');
    sessionStorage.removeItem('active_ella_radio');
  }, []);

  // Global keyboard shortcuts: spacebar + media keys (Mac F7/F8/F9, Windows media keys)
  useEffect(() => {
    function onKeyDown(e) {
      switch (e.key) {
        case 'MediaPlayPause': {
          e.preventDefault();
          const ps = useMaestroStore.getState().playbackState;
          if (ps === 'playing') TransportCommand.pause(true);
          else if (!blockBitPerfectPlay()) TransportCommand.play();
          return;
        }
        case 'MediaTrackNext': {
          e.preventDefault();
          QueueController.next();
          return;
        }
        case 'MediaTrackPrevious': {
          e.preventDefault();
          QueueController.previous();
          return;
        }
        case 'MediaStop': {
          e.preventDefault();
          TransportCommand.pause(true);
          return;
        }
        case 'MediaPause': {
          e.preventDefault();
          TransportCommand.pause(true);
          return;
        }
        case 'MediaPlay': {
          e.preventDefault();
          if (!blockBitPerfectPlay()) TransportCommand.play();
          return;
        }
        default:
          break;
      }

      if (e.code !== 'Space') return;
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.target?.isContentEditable) return;
      e.preventDefault();
      const ps = useMaestroStore.getState().playbackState;
      if (ps === 'playing') {
        TransportCommand.pause(true);
      } else if (ps === 'paused' || ps === 'stopped') {
        if (!blockBitPerfectPlay()) TransportCommand.play();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [blockBitPerfectPlay]);

  const playQueueTrack = useCallback((idx) => {
    QueueController.playQueueTrack(idx);
  }, []);

  // --- Scrub handlers ---
  const startScrub = useCallback(() => {
    setIsScrubbing(true);
    if (isScrubbingRef) isScrubbingRef.current = true;
    getClientTransport().lockPosition();
    setScrubTime(position);
  }, [position, isScrubbingRef]);

  const doScrub = useCallback((e) => setScrubTime(parseInt(e.target.value)), []);

  const endScrub = useCallback((e) => {
    const val = parseInt(e.target.value);
    setScrubTime(val);
    handleSeek(val);
    setTimeout(() => {
      setIsScrubbing(false);
      if (isScrubbingRef) isScrubbingRef.current = false;
      getClientTransport().unlockPosition();
    }, 500);
  }, [handleSeek, isScrubbingRef]);

  // --- Zone selection (delegated to ZoneController) ---
  const selectZone = useCallback(async (zone) => {
    await ZoneController.selectZone(zone);
    setShowDevicePicker(false);
  }, [setShowDevicePicker]);

  // --- Navigate to album ---
  const goToAlbum = useCallback(() => {
    if (!currentTrack?.album) return;
    const isLocal = currentTrack.source === 'local_library';
    nav.openAlbum({
      id: currentTrack.albumId || currentTrack.id,
      title: currentTrack.album,
      source: currentTrack.albumId ? currentTrack.source : undefined,
      __source: isLocal ? 'local_library' : currentTrack.source,
      isLocalLibrary: isLocal,
      album_key: currentTrack.album_key || null,
      artist: currentTrack.artist,
      artistName: currentTrack.artist,
      cover: currentTrack.artworkUrl,
      serviceAlbumId: isLocal ? null : (currentTrack.albumId || null),
      bitDepth: currentTrack.bitDepth,
      sampleRate: currentTrack.sampleRate,
    });
  }, [currentTrack, nav]);

  // ── Shared volume + zone + queue controls (right rail) ──
  const renderQueueButton = (iconSize = 24, badgeSize = 'w-4 h-4', badgeText = 'text-[8px]') => (
    <button
      data-guide-id="queue-button"
      onClick={() => setShowQueueSheet(!showQueueSheet)}
      className="relative transition hover:opacity-80 active:scale-90"
      style={{ color: showQueueSheet ? GOLD : 'var(--text-secondary)' }}
      aria-label="Queue"
    >
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" /></svg>
      {queue.length > 0 && (
        <span className={`absolute -top-1 -right-1 ${badgeSize} rounded-full ${badgeText} font-bold flex items-center justify-center`} style={{ background: GOLD, color: '#1a1a2e' }}>
          {Math.min(queue.length, 99)}
        </span>
      )}
    </button>
  );

  const renderSignalPathNudge = () => (
    signalPathNudge.visible ? (
      <div className="fixed bottom-[80px] z-[118] px-4 pb-2" style={{ left: 'var(--sidebar-docked-width, 0px)', right: 0, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="rounded-lg p-3 flex items-start gap-3" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <div className="flex-1 min-w-0">
            <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
              {t('signal_path.nudge_body')}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={signalPathNudge.onShowMe}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-md"
              style={{ background: GOLD, color: '#111' }}
            >
              {t('signal_path.nudge_show_me')}
            </button>
            <button
              type="button"
              onClick={signalPathNudge.onNotNow}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-md"
              style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}
            >
              {t('signal_path.nudge_not_now')}
            </button>
          </div>
        </div>
      </div>
    ) : null
  );

  const renderRightControls = () => (
    <div className="flex-shrink-0 flex items-center gap-5 px-5" style={{ borderLeft: '1px solid var(--border)' }}>
      <div className="flex items-center gap-5" data-guide-id="device-volume">
        <button onClick={() => setShowDevicePicker(!showDevicePicker)} data-guide-id="device-picker" className="flex items-center gap-2 transition hover:opacity-80" style={{ color: 'var(--text-secondary)' }} aria-label="Output Device">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="4" y="2" width="16" height="20" rx="2" />
            <circle cx="12" cy="14" r="3" />
            <circle cx="12" cy="6" r="1.5" />
          </svg>
          <span className="text-[11px] font-medium truncate" style={{ maxWidth: 140 }}>{deviceLabel}</span>
          <SignalPathBadge zone={activeZone} />
        </button>

        {multizoneEnabled && mzGroupedZones.length >= 2 ? (
          <MultiZoneVolumePanel
            masterVolume={mzMasterVolume}
            groupedZones={mzGroupedZones}
            onMasterVolumeChange={setMzMasterVolume}
            onZoneVolumeChange={setMzZoneVolume}
            isOpen={showMzVolume}
            onToggle={toggleMzVolume}
          />
        ) : fixedVolume ? (
          <div className="flex items-center gap-1.5" style={{ color: 'var(--text-tertiary)' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span className="text-[10px] font-medium whitespace-nowrap">Fixed Volume</span>
          </div>
        ) : (
          <div className="relative flex items-center">
            <button onClick={() => setShowVolume(!showVolume)} data-guide-id="volume-control" className="flex items-center gap-1.5 transition hover:opacity-80" style={{ color: 'var(--text-secondary)' }} aria-label="Volume">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 9V15H7L12 20V4L7 9H3Z" />
                {displayVolume > 0 && <path d="M14 11.5C14 10.67 13.55 9.96 12.88 9.58V13.42C13.55 13.04 14 12.33 14 11.5Z" />}
                {displayVolume > 50 && <path d="M16.54 8.46C17.48 9.4 18 10.67 18 12C18 13.33 17.48 14.6 16.54 15.54L15.12 14.12C15.73 13.51 16.13 12.69 16.13 11.77C16.13 10.85 15.73 10.03 15.12 9.42L16.54 8.46Z" />}
              </svg>
              <span className="text-[11px] tabular-nums font-medium">{displayVolume}</span>
            </button>
            {showVolume && renderVolumePopover()}
          </div>
        )}
      </div>
      {renderQueueButton()}
      {isBridgeActive && connectorVersion && (
        <span className="text-[9px] tabular-nums" style={{ color: 'var(--text-tertiary)' }}>v{connectorVersion}</span>
      )}
    </div>
  );

  const renderDevicePicker = () =>
    showDevicePicker && (
      <MultiZoneDevicePicker
        allZones={allZones}
        batonZones={batonZones}
        uniqueNetworkZones={uniqueNetworkZones}
        activeZone={activeZone}
        zoneName={zoneName}
        audioOutputs={audioOutputs}
        isNativeiOS={isNativeiOS}
        multizoneEnabled={multizoneEnabled}
        groupedZones={mzGroupedZones}
        onSelectZone={selectZone}
        onToggleZone={toggleZoneInGroup}
        onEnableMultizone={enableMultizone}
        onDisableMultizone={disableMultizone}
        onClose={() => setShowDevicePicker(false)}
      />
    );

  const handleSwitchToSystemAudio = useCallback(() => {
    useMaestroStore.getState().setExclusiveError(null);
    const localZone = { id: 'this_device', name: 'System Audio', type: 'local' };
    ZoneController.selectZone(localZone);
  }, []);

  /* ─── Idle state ───────────────────────────────────────────────── */
  if (source === 'idle' && !displayTrack) {
    return (
      <>
      <div className="fixed bottom-0 z-[110]" style={{ left: 'var(--sidebar-docked-width, 0px)', right: 0, paddingBottom: 'env(safe-area-inset-bottom, 0px)', background: 'var(--bg-primary)' }}>
        <div ref={miniPlayerMeasureRef}>
          <div className="mini-player-container" data-guide-id="mini-player" style={{ background: 'var(--bg-primary)', borderTop: '1px solid var(--border)' }}>
            <div className="flex items-center" style={{ padding: '6px 0', minHeight: 72 }}>
              <div className="flex-shrink-0" style={{ width: isNarrow ? 56 : 88 }} />

              <div className="flex-1 min-w-0 flex flex-col items-center justify-center px-3">
                <div className="relative flex items-center justify-center gap-5" style={{ width: '100%', maxWidth: 280 }}>
                  <button disabled style={{ color: 'var(--text-tertiary)', opacity: 0.4 }}>
                    <svg width={isNarrow ? 24 : 22} height={isNarrow ? 24 : 22} viewBox="0 0 24 24" fill="currentColor"><path d="M19 20L9 12L19 4V20Z" /><path d="M5 19V5H7V19H5Z" /></svg>
                  </button>
                  <div className="rounded-full flex items-center justify-center" style={{ width: isNarrow ? 42 : 46, height: isNarrow ? 42 : 46, background: 'var(--bg-secondary)' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--text-tertiary)" style={{ marginLeft: 2, opacity: 0.5 }}><path d="M8 5V19L19 12L8 5Z" /></svg>
                  </div>
                  <button disabled style={{ color: 'var(--text-tertiary)', opacity: 0.4 }}>
                    <svg width={isNarrow ? 24 : 22} height={isNarrow ? 24 : 22} viewBox="0 0 24 24" fill="currentColor"><path d="M5 4L15 12L5 20V4Z" /><path d="M19 5V19H17V5H19Z" /></svg>
                  </button>
                  {isNarrow && (
                    <div className="flex items-center gap-4" style={{ position: 'absolute', right: -8, top: '50%', transform: 'translateY(-50%)' }}>
                      <button onClick={() => setShowDevicePicker(!showDevicePicker)} data-guide-id="device-picker" className="transition active:scale-90" style={{ color: showDevicePicker ? GOLD : 'var(--text-secondary)' }} aria-label="Output Device">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="4" y="2" width="16" height="20" rx="2" />
                          <circle cx="12" cy="14" r="3" />
                          <circle cx="12" cy="6" r="1.5" />
                        </svg>
                      </button>
                      {renderQueueButton(24, 'w-3.5 h-3.5', 'text-[7px]')}
                    </div>
                  )}
                </div>
                {!isNarrow && isSecurelyConnected && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#4CAF50', flexShrink: 0 }} />
                    <span style={{ fontSize: 9, color: 'var(--text-tertiary)' }}>Secured · Home library connected</span>
                  </div>
                )}
              </div>

              {isNarrow ? (
                <div className="flex-shrink-0 flex items-center justify-center px-3">
                  <div className="relative">
                    <button onClick={() => setShowVolume(!showVolume)} data-guide-id="volume-control" className="transition active:scale-90" style={{ color: 'var(--text-secondary)' }} aria-label="Volume">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 9V15H7L12 20V4L7 9H3Z" />
                        {displayVolume > 0 && <path d="M14 11.5C14 10.67 13.55 9.96 12.88 9.58V13.42C13.55 13.04 14 12.33 14 11.5Z" />}
                        {displayVolume > 50 && <path d="M16.54 8.46C17.48 9.4 18 10.67 18 12C18 13.33 17.48 14.6 16.54 15.54L15.12 14.12C15.73 13.51 16.13 12.69 16.13 11.77C16.13 10.85 15.73 10.03 15.12 9.42L16.54 8.46Z" />}
                      </svg>
                    </button>
                    {showVolume && renderVolumePopover()}
                  </div>
                </div>
              ) : (
                renderRightControls()
              )}
            </div>
          </div>
          <PeerPresenceBar remotePeers={remotePeers} />
        </div>
        {renderDevicePicker()}
      </div>
      <QueuePanel
        isOpen={showQueueSheet}
        onClose={() => setShowQueueSheet(false)}
        onPlayIndex={playQueueTrack}
      />
      </>
    );
  }

  /* ─── Active player ────────────────────────────────────────────── */
  return (
    <>
      {renderSignalPathNudge()}
      {unreachablePlayPrompt && (
        <div className="fixed bottom-[80px] z-[120] px-4 pb-2" style={{ left: 'var(--sidebar-docked-width, 0px)', right: 0, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="rounded-lg p-3 flex items-start gap-3" style={{ background: 'rgba(40, 40, 40, 0.95)', backdropFilter: 'blur(12px)', border: '1px solid var(--border)' }}>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                {t('player.unreachable_play_prompt', { device: unreachablePlayPrompt.deviceName })}
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={unreachablePlayPrompt.onConfirm}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-md"
                style={{ background: GOLD, color: '#111' }}
              >
                {t('player.unreachable_play_confirm')}
              </button>
              <button
                onClick={unreachablePlayPrompt.onCancel}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-md"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}
              >
                {t('player.unreachable_play_cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {exclusiveError && (
        <div className="fixed bottom-[80px] z-[120] px-4 pb-2" style={{ left: 'var(--sidebar-docked-width, 0px)', right: 0, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="rounded-lg p-3 flex items-start gap-3" style={{ background: 'rgba(180, 50, 50, 0.95)', backdropFilter: 'blur(12px)' }}>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-white">
                {isExclusiveHogUnavailableError(exclusiveError)
                  ? t('exclusive.gate_error_title')
                  : t('exclusive.format_error_title')}
              </p>
              <p className="text-[11px] text-white/80 mt-0.5">{exclusiveError}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={handleSwitchToSystemAudio} className="text-[11px] font-semibold px-3 py-1.5 rounded-md" style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}>
                {t('exclusive.switch_system_audio')}
              </button>
              <button onClick={() => useMaestroStore.getState().setExclusiveError(null)} className="text-white/60 px-1">✕</button>
            </div>
          </div>
        </div>
      )}
      {usbOutputFallbackNotice && selectedUsbDac && !exclusiveError && (
        <div className="fixed bottom-[80px] z-[119] px-4 pb-2" style={{ left: 'var(--sidebar-docked-width, 0px)', right: 0, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="rounded-lg p-3 flex items-start gap-3" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <div className="flex-1 min-w-0">
              <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                {usbOutputFallbackNotice.reason === 'device_busy'
                  ? t('exclusive.fallback_notice_busy')
                  : t('exclusive.fallback_notice', {
                      rate: usbOutputFallbackNotice.sampleRate,
                      depth: usbOutputFallbackNotice.bitDepth,
                    })}
              </p>
            </div>
            <button
              onClick={() => useMaestroStore.getState().setUsbOutputFallbackNotice(null)}
              className="text-[11px] flex-shrink-0 px-1"
              style={{ color: 'var(--text-tertiary)' }}
              aria-label={t('common.close')}
            >
              ✕
            </button>
          </div>
        </div>
      )}
      <div className="fixed bottom-0 z-[110]" style={{ left: 'var(--sidebar-docked-width, 0px)', right: 0, paddingBottom: 'env(safe-area-inset-bottom, 0px)', background: 'var(--bg-primary)' }}>
        <div ref={miniPlayerMeasureRef}>
          <div className="mini-player-container" data-guide-id="mini-player" style={{ background: 'var(--bg-primary)', borderTop: '1px solid var(--border)' }}>
          <div className="flex items-center" style={{ padding: '6px 0' }}>

            {/* LEFT: Album art */}
            <button type="button" onClick={goToAlbum} className="flex-shrink-0 flex items-center justify-center" style={{ width: isNarrow ? 56 : 88 }}>
              {artworkUrl ? (
                <img src={artworkUrl} alt="" style={{ width: isNarrow ? 44 : 60, height: isNarrow ? 44 : 60, objectFit: 'cover', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }} />
              ) : (
                <div style={{ width: isNarrow ? 44 : 60, height: isNarrow ? 44 : 60, background: 'var(--bg-secondary)', borderRadius: 8 }} />
              )}
            </button>

            {/* CENTER: info + timeline + transport + playing-on */}
            <div className="flex-1 min-w-0 flex flex-col px-3">
              <div className="flex items-center gap-2">
                <p className={`${isNarrow ? 'text-[14px]' : 'text-[12px]'} font-semibold truncate`} style={{ color: 'var(--text-primary)' }}>{displayTrack?.title}</p>
                {!isNarrow && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 uppercase whitespace-nowrap" style={{ color: '#C9A84C', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.25)' }}>{formatBadge}</span>}
              </div>
              <p className={`${isNarrow ? 'text-[12px]' : 'text-[10px]'} truncate`} style={{ color: 'var(--text-secondary)', marginTop: 1 }}>{displayTrack?.artist}</p>
              {transportPlaybackError && (
                <p className={`${isNarrow ? 'text-[11px]' : 'text-[10px]'} truncate font-medium`} style={{ color: '#ef4444', marginTop: 2 }}>
                  {transportPlaybackError}
                </p>
              )}
              {isObserving && remoteSession?.playbackState === 'playing' && (
                <p className="text-[9px] truncate" style={{ color: '#C9A84C', marginTop: 1, fontStyle: 'italic' }}>
                  {t('player.playing_on', { zone: remoteSession.rendererName || '' }) || `Playing on ${remoteSession.rendererName}`}
                </p>
              )}

              {/* Timeline — seek input must stay inside this box; negative/inset-0 spill was overlapping transport and eating play/pause taps on iOS */}
              <div className="flex items-center gap-1.5 relative z-0" style={{ marginTop: 4 }}>
                <span className={`${isNarrow ? 'text-[10px]' : 'text-[9px]'} tabular-nums`} style={{ color: 'var(--text-tertiary)', minWidth: 24 }}>{fmt(displayTime)}</span>
                <div
                  className="flex-1 relative"
                  style={{ height: isNarrow ? 28 : 16 }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      height: isNarrow ? 5 : 3,
                      borderRadius: isNarrow ? 3 : 2,
                      background: 'var(--border)',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: 0,
                      transform: 'translateY(-50%)',
                      height: isNarrow ? 5 : 3,
                      width: `${progressPct}%`,
                      borderRadius: isNarrow ? 3 : 2,
                      background: '#C9A84C',
                      transition: isScrubbing ? 'none' : 'width 0.3s linear',
                    }}
                  />
                  <input
                    type="range"
                    min="0"
                    max={scrubberMax}
                    value={displayTime}
                    onMouseDown={startScrub}
                    onTouchStart={startScrub}
                    onMouseUp={endScrub}
                    onTouchEnd={endScrub}
                    onInput={doScrub}
                    disabled={source === 'idle'}
                    className="absolute inset-0 w-full opacity-0 cursor-pointer"
                    style={{ touchAction: 'none', height: '100%', margin: 0 }}
                    aria-label="Seek"
                  />
                </div>
                <span className={`${isNarrow ? 'text-[10px]' : 'text-[9px]'} tabular-nums text-right`} style={{ color: 'var(--text-tertiary)', minWidth: 24 }}>-{fmt(Math.max(0, (effectiveDuration || 0) - displayTime))}</span>
              </div>

              <div className="relative z-10" style={{ marginTop: isNarrow ? 6 : 4, opacity: audioStreamBuffering ? 0.55 : 1, transition: 'opacity 0.2s ease' }}>
                {audioStreamBuffering && (
                  <span
                    title="Buffering"
                    aria-hidden
                    style={{
                      position: 'absolute',
                      top: -2,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: GOLD,
                      opacity: 0.85,
                    }}
                  />
                )}
                <div className="flex items-center justify-center gap-5">
                  {!isNarrow && !isRadioActive && (
                    <button type="button" onClick={handleToggleShuffle} className="transition active:scale-90" style={{ color: shuffled ? GOLD : 'var(--text-tertiary)' }} aria-label={shuffled ? 'Shuffle on' : 'Shuffle off'} title={shuffled ? 'Shuffle on' : 'Shuffle off'}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>
                    </button>
                  )}
                  <button type="button" onClick={handlePrev} disabled={!canPrev} className="transition active:scale-90" style={{ color: 'var(--text-secondary)', opacity: canPrev ? 1 : 0.4 }}><svg width={isNarrow ? 24 : 20} height={isNarrow ? 24 : 20} viewBox="0 0 24 24" fill="currentColor"><path d="M19 20L9 12L19 4V20Z" /><path d="M5 19V5H7V19H5Z" /></svg></button>
                  <button type="button" onClick={handlePlayPause}
                    className="rounded-full flex items-center justify-center transition active:scale-90 touch-manipulation"
                    style={{ width: isNarrow ? 42 : 36, height: isNarrow ? 42 : 36, background: 'var(--text-primary)', color: 'var(--bg-primary)' }}
                    aria-label={isPlaying ? t('player.pause') : t('player.play')}>
                    {isPlaying
                      ? <svg width={isNarrow ? 20 : 18} height={isNarrow ? 20 : 18} viewBox="0 0 24 24" fill="currentColor"><path d="M6 19H10V5H6V19ZM14 5V19H18V5H14Z" /></svg>
                      : <svg width={isNarrow ? 20 : 18} height={isNarrow ? 20 : 18} viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 2 }}><path d="M8 5V19L19 12L8 5Z" /></svg>}
                  </button>
                  <button type="button" onClick={handleNext} disabled={!canSkip} className="transition active:scale-90" style={{ color: 'var(--text-secondary)', opacity: canSkip ? 1 : 0.4 }}><svg width={isNarrow ? 24 : 20} height={isNarrow ? 24 : 20} viewBox="0 0 24 24" fill="currentColor"><path d="M5 4L15 12L5 20V4Z" /><path d="M19 5V19H17V5H19Z" /></svg></button>
                </div>
                {/* Speaker + Queue right-aligned on iPhone — absolute so they don't shift play/pause center */}
                {isNarrow && (
                  <div className="flex items-center gap-4" style={{ position: 'absolute', right: -8, top: '50%', transform: 'translateY(-50%)' }}>
                    <button onClick={() => setShowDevicePicker(!showDevicePicker)} data-guide-id="device-picker" className="transition active:scale-90" style={{ color: showDevicePicker ? GOLD : 'var(--text-secondary)' }} aria-label="Output Device">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="4" y="2" width="16" height="20" rx="2" />
                        <circle cx="12" cy="14" r="3" />
                        <circle cx="12" cy="6" r="1.5" />
                      </svg>
                    </button>
                      {renderQueueButton(24, 'w-3.5 h-3.5', 'text-[7px]')}
                  </div>
                )}
              </div>

              {/* Playing-on indicator — hidden on narrow */}
              {!isNarrow && (
                <div className="text-center" style={{ marginTop: 2 }}>
                  {multizoneEnabled && mzGroupedZones.length >= 2 ? (
                    <span className="text-[8px] uppercase font-semibold tracking-widest" style={{ color: '#C9A84C' }}>
                      Playing on {mzGroupedZones.length} zones
                    </span>
                  ) : sessionStorage.getItem('active_smart_playlist') ? (
                    <span className="text-[8px] uppercase font-semibold tracking-widest" style={{ color: '#C9A84C' }}>✦ Ella's Pick · {deviceLabel}</span>
                  ) : sessionStorage.getItem('active_ella_radio') ? (
                    <span className="text-[8px] uppercase font-semibold tracking-widest" style={{ color: '#C9A84C' }}>✦ Ella Radio · {deviceLabel}</span>
                  ) : (
                    <span className="text-[8px] uppercase font-semibold tracking-widest inline-flex items-center justify-center gap-1.5 flex-wrap" style={{ color: '#C9A84C' }}>
                      <span>Playing on {deviceLabel}</span>
                      <SignalPathBadge zone={activeZone} />
                    </span>
                  )}
                </div>
              )}

              {!isNarrow && isSecurelyConnected && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, justifyContent: 'center' }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#4CAF50', flexShrink: 0 }} />
                  <span style={{ fontSize: 9, color: 'var(--text-tertiary)' }}>Secured · Home library connected</span>
                </div>
              )}
            </div>

            {/* RIGHT: volume only on iPhone (speaker + queue moved to transport row) */}
            {isNarrow ? (
              <div className="flex-shrink-0 flex items-center justify-center px-3">
                <div className="relative">
                  <button onClick={() => setShowVolume(!showVolume)} data-guide-id="volume-control" className="transition active:scale-90" style={{ color: 'var(--text-secondary)' }} aria-label="Volume">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 9V15H7L12 20V4L7 9H3Z" />
                      {displayVolume > 0 && <path d="M14 11.5C14 10.67 13.55 9.96 12.88 9.58V13.42C13.55 13.04 14 12.33 14 11.5Z" />}
                      {displayVolume > 50 && <path d="M16.54 8.46C17.48 9.4 18 10.67 18 12C18 13.33 17.48 14.6 16.54 15.54L15.12 14.12C15.73 13.51 16.13 12.69 16.13 11.77C16.13 10.85 15.73 10.03 15.12 9.42L16.54 8.46Z" />}
                    </svg>
                  </button>
                  {showVolume && renderVolumePopover()}
                </div>
              </div>
            ) : (
              renderRightControls()
            )}
          </div>
        </div>

        <PeerPresenceBar remotePeers={remotePeers} />
        </div>
        {renderDevicePicker()}
      </div>

      <QueuePanel
        isOpen={showQueueSheet}
        onClose={() => setShowQueueSheet(false)}
        onPlayIndex={playQueueTrack}
      />
    </>
  );
}
